import axios from "axios";
import { getUserAndPremiseId } from "../utils";
import { getPremiseId } from "../utils/worker";
import { v4 as uuidv4 } from "uuid";
let userData = JSON.parse(localStorage.getItem("userinfo"));
// const {accessToken} = userData;
const getUserInfo = getUserAndPremiseId();
const userId = getUserInfo?.userId;

const premiseId = getPremiseId();

// Store active requests to prevent duplicates
const activeRequests = new Map();

// Clear stale requests after 30 seconds (fallback cleanup)
const clearStaleRequests = () => {
  const now = Date.now();
  for (const [key, value] of activeRequests.entries()) {
    if (value.timestamp && now - value.timestamp > 30000) {
      activeRequests.delete(key);
    }
  }
};

// Initialize cleanup interval only once
let cleanupInterval = null;
const initializeCleanup = () => {
  if (!cleanupInterval) {
    cleanupInterval = setInterval(clearStaleRequests, 60000);
  }
};

// Helper function to create deduplication wrapper for API methods
const createDedupedRequest = (requestFunction, method, url, params = null, data = null, options = null) => {
  // Initialize cleanup on first use
  initializeCleanup();

  // Skip deduplication if using AbortController signal (manual cancellation control)
  if (options?.signal) {
    return requestFunction();
  }

  // Create a unique key for this request
  const requestKey = `${method}_${url}_${JSON.stringify(params || {})}_${JSON.stringify(data || {})}`;

  // Check if this request is already in progress
  if (activeRequests.has(requestKey)) {
    return activeRequests.get(requestKey).promise;
  }

  // Create the request promise
  const requestPromise = requestFunction()
    .then((response) => {
      // Remove from active requests when completed
      activeRequests.delete(requestKey);
      return response;
    })
    .catch((error) => {
      // Remove from active requests when completed (even on error)
      activeRequests.delete(requestKey);
      throw error;
    });

  // Store the promise with timestamp to prevent duplicate requests
  activeRequests.set(requestKey, {
    promise: requestPromise,
    timestamp: Date.now()
  });

  return requestPromise;
};

const API = {
  Api: (url) => {
    return {
      NodeGetWithPremise: (params) =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
              },
              params: {
                ...params,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url,
          params
        ),
      NodeGetWithCustomHeader: (customHeader) =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
                ...customHeader,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url,
          null,
          customHeader
        ),

      NodePutPremiseIdHeader: (data) =>
        createDedupedRequest(
          () => axios
            .put(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PUT",
          url,
          null,
          data
        ),

      NodePostPremiseIdHeader: (data) =>
        createDedupedRequest(
          () => axios
            .post(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "POST",
          url,
          null,
          data
        ),
      getWithoutBearer: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url
        ),
      get: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url
        ),
      getWithHeader: (options = {}) =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                token: userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
              signal: options?.signal,
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                throw error;
              }
              return { response: error.response, status: false };
            }),
          "GET",
          url,
          null,
          null,
          options
        ),
      getWithPremiseHeader: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                token: userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                type: 2,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url
        ),
      getWithUserNPremiseHeader: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "USER-ID": userId,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url
        ),
      getWithUserNPremiseHeaderPincode: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                authorization: "Bearer " + userData?.accessToken,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url
        ),
      getRegexPatternHeader: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url
        ),
      getWithPremiseIdHeader: (options) => {
        const config = {
          headers: {
            Authorization: "Bearer " + userData?.accessToken,
            "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
            "X-Request-ID": uuidv4(),
          }
        };

        // Add signal if provided
        if (options?.signal) {
          config.signal = options.signal;
        }

        return createDedupedRequest(
          () => axios
            .get(url, config)
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              // Check if request was cancelled
              if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                throw error; // Re-throw cancellation errors to be caught by component
              }
              return { response: error.response, status: false };
            }),
          "GET",
          url,
          null,
          null,
          options  // Pass options to check for signal
        );
      },
      getWithAllHeaders: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "USER-ID": userId,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                token: userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url
        ),
      getWithAllHeadersDP: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                clientname: "HUBOPS",
                "x-api-key": "61757468746f6b656e666f726875626f7073",
                authtype: "x-api-key",
                version: "1.0",

              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url
        ),
      getCustomHeader: (headersOrOptions = {}) => {
        // Handle backward compatibility - if first param is not an object with signal, treat it as headers
        const isOptionsObject = headersOrOptions && typeof headersOrOptions === "object" && "signal" in headersOrOptions;
        const options = isOptionsObject ? headersOrOptions : { headers: headersOrOptions };

        return createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
                ...options.headers,
              },
              signal: options.signal, // Add AbortController signal support
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url,
          null,
          options
        );
      },
      getCustomHeaderWithParams: (headers, params) =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
                ...headers,
              },
              params: {
                ...params,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url,
          params,
          headers
        ),
      getwithParams: (params) =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "USER-ID": userId,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
              },
              params: {
                ...params,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url,
          params
        ),
      getBagDetailsParams: (params) =>
        createDedupedRequest(
          () => axios
            .get(url.trim(), {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
              },
              params: {
                ...params,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url.trim(),
          params
        ),
      getwithParamsAuditReport: (params, options = {}) =>
        createDedupedRequest(
          () => axios
            .get(url, {
              params: {
                ...params,
              },
              signal: options.signal, // Add AbortController signal support
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "GET",
          url,
          params,
          null,
          options
        ),
      post: (data) =>
        createDedupedRequest(
          () => axios
            .post(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "POST",
          url,
          null,
          data
        ),
      postWithHeader: (data) =>
        createDedupedRequest(
          () => axios
            .post(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                token: userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "POST",
          url,
          null,
          data
        ),
      postWithHeaderOptions: (data, options = {}) =>
        createDedupedRequest(
          () => axios
            .post(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                token: userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
              signal: options.signal, // Add AbortController signal support
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "POST",
          url,
          null,
          data
        ),
      postWithHeaderDP: (data) =>
        createDedupedRequest(
          () => axios
            .post(url, data, {
              headers: {
                clientname: "HUBOPS",
                "x-api-key": "61757468746f6b656e666f726875626f7073",
                authtype: "x-api-key",
                version: "1.0",

              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "POST",
          url,
          null,
          data
        ),
      postCustomHeader: (data, headers) =>
        createDedupedRequest(
          () => axios
            .post(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
                ...headers,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "POST",
          url,
          null,
          { data, headers }
        ),
      postWithPrmiseHeader: (data) =>
        createDedupedRequest(
          () => axios
            .post(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "POST",
          url,
          null,
          data
        ),

      postIdHeader: (data, options) => {
        const config = {
          headers: {
            Authorization: "Bearer " + userData?.accessToken,
            "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
            "USER-ID": userId,
            "X-Request-ID": uuidv4(),
          }
        };

        // Add signal if provided
        if (options?.signal) {
          config.signal = options.signal;
        }

        return createDedupedRequest(
          () => axios
            .post(url, data, config)
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              // Check if request was cancelled
              if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                throw error; // Re-throw cancellation errors to be caught by component
              }
              return { response: error.response, status: false };
            }),
          "POST",
          url,
          null,
          data,
          options  // Pass options to check for signal
        );
      },
      put: (data) =>
        createDedupedRequest(
          () => axios
            .put(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PUT",
          url,
          null,
          data
        ),
      putWithHeaderDP: (data) =>
        createDedupedRequest(
          () => axios
            .put(url, data, {
              headers: {
                "Content-Type": "application/json",
                clientname: "HUBOPS",
                "x-api-key": "61757468746f6b656e666f726875626f7073",
                authtype: "x-api-key",
                version: "1.0",
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PUT",
          url,
          null,
          data
        ),
      putCustomHeader: (data, headers) =>
        createDedupedRequest(
          () => axios
            .put(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
                ...headers,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PUT",
          url,
          null,
          { data, headers }
        ),
      putCustomIdHeader: (data, customHeader) =>
        createDedupedRequest(
          () => axios
            .put(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "USER-ID": userId,
                "X-Request-ID": uuidv4(),
                ...customHeader,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PUT",
          url,
          null,
          { data, customHeader }
        ),
      putWithHeader: (data) =>
        createDedupedRequest(
          () => axios
            .put(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                token: userData?.accessToken,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PUT",
          url,
          null,
          data
        ),
      putIdHeader: (data) =>
        createDedupedRequest(
          () => axios
            .put(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "USER-ID": userId,
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PUT",
          url,
          null,
          data
        ),
      putPremiseIdHeader: (data) =>
        createDedupedRequest(
          () => axios
            .put(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PUT",
          url,
          null,
          data
        ),
      patchCustomHeader: (data, headers) =>
        createDedupedRequest(
          () => axios
            .patch(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "X-Request-ID": uuidv4(),
                ...headers,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PATCH",
          url,
          null,
          { data, headers }
        ),
      patchIdHeader: (data) =>
        createDedupedRequest(
          () => axios
            .patch(url, data, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
                "USER-ID": userId,
              },
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "PATCH",
          url,
          null,
          data
        ),
      deleteIdHeader: (data) =>
        createDedupedRequest(
          () => axios
            .delete(url, {
              headers: {
                Authorization: "Bearer " + userData?.accessToken,
                "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
                "X-Request-ID": uuidv4(),
                "USER-ID": userId,
              },
              data,
            })
            .then((response) => {
              return { response: response, status: true };
            })
            .catch((error) => {
              return { response: error.response, status: false };
            }),
          "DELETE",
          url,
          null,
          data
        ),
    };
  },
  ApiWithAuth: (url, token) => {
    return {
      get: () =>
        createDedupedRequest(
          () => axios
            .get(url, {
              headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
                "X-Request-ID": uuidv4(),
                Authorization: token,
              },
            })
            .then((response) => {
              return response?.data;
            })
            .catch((error) => {
              return error.response;
            }),
          "GET",
          url,
          null,
          { token }
        ),
      post: (data) =>
        createDedupedRequest(
          () => axios
            .post(url, data, {
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
                "X-Request-ID": uuidv4(),
                Authorization: token,
              },
            })
            .then((response) => {
              return response?.data;
            })
            .catch((error) => {
              return error.response;
            }),
          "POST",
          url,
          null,
          { data, token }
        ),
    };
  },
  /*getBagDetailView: async (lcrNum, type) => {
    try {
      const token = userData?.accessToken;
      const response = await axios.get(urls.BAG_DETAIL_VIEW + `?lcrNum=${lcrNum}&type=${type}`,
        {
          headers: {
            Authorization: "Bearer " + token,
            "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
            "X-Request-ID": uuidv4(),
          },
        }
      );
      return { response: response.data, status: true };
    } catch (error) {
      return { response: error.response, status: false };
    }
  },
  getShipmentDetailView: async (lcrNum, type) => {
    try {
      const token = userData?.accessToken;
      const response = await axios.get(urls.SHIPMENT_DETAIL_VIEW + `?lcrNum=${lcrNum}&type=${type}`,
        {
          headers: {
            Authorization: "Bearer " + token,
            "SMCS-PREMISE-ID": premiseId.split("Bearer")[1],
            "X-Request-ID": uuidv4(),
          },
        }
      );
      return { response: response.data, status: true };
    } catch (error) {
      return { response: error.response, status: false };
    }
  },*/
};
export default API;
